import React, { useEffect, useState } from "react";
import { useClientInfo } from "../../utils/commonImports";
import axiosInstance from "../../api/axiosInstance";

import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";


export default function WhatsAppIntegrationForm() {

  const { clientId } =
    useClientInfo();

  const [form, setForm] = useState({
    waba_id: "",
    phone_number_id: "",
    business_name: "",
    display_phone_number: "",
    access_token: "",
  });

  const [connected, setConnected] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [showSuccess, setShowSuccess] =
    useState(false);

  const [errorMessage, setErrorMessage] =
    useState("");


  /*
  |--------------------------------------------------------------------------
  | LOAD SETTINGS
  |--------------------------------------------------------------------------
  */
  useEffect(() => {

    if (!clientId) return;

    loadSettings();

  }, [clientId]);


  const loadSettings = async () => {

    try {

      const response =
        await axiosInstance.get(
          `/api/whatsapp-cloud/settings/${clientId}`
        );

      const data =
        response.data;

      if (data.settings) {

        setForm({
          waba_id:
            data.settings.waba_id || "",

          phone_number_id:
            data.settings.phone_number_id || "",

          business_name:
            data.settings.business_name || "",

          display_phone_number:
            data.settings.display_phone_number || "",

          /*
           * Token is NEVER returned by backend.
           */
          access_token: "",
        });

      }

      setConnected(
        data.connected === true
      );

    } catch (error) {

      console.error(
        "WhatsApp settings load error:",
        error
      );

    }
  };


  /*
  |--------------------------------------------------------------------------
  | INPUT CHANGE
  |--------------------------------------------------------------------------
  */
  const handleChange = (e) => {

    const {
      name,
      value,
    } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

  };


  /*
  |--------------------------------------------------------------------------
  | SAVE
  |--------------------------------------------------------------------------
  */
  const handleSave = async () => {

    if (!clientId) {
      setErrorMessage(
        "Client ID is missing"
      );
      return;
    }

    if (!form.phone_number_id.trim()) {
      setErrorMessage(
        "Phone Number ID is required"
      );
      return;
    }

    /*
     * For a new connection token is required.
     * For updating existing settings, backend
     * preserves existing token.
     */
    if (
      !connected &&
      !form.access_token.trim()
    ) {
      setErrorMessage(
        "Access Token is required"
      );
      return;
    }

    setLoading(true);

    try {

      const response =
        await axiosInstance.post(
          "/api/whatsapp-cloud/settings",
          {
            client_id:
              clientId,

            waba_id:
              form.waba_id,

            phone_number_id:
              form.phone_number_id,

            business_name:
              form.business_name,

            display_phone_number:
              form.display_phone_number,

            access_token:
              form.access_token || null,
          }
        );

      if (
        response.data?.success
      ) {

        setShowSuccess(true);

        /*
         * Clear token from browser state.
         */
        setForm((prev) => ({
          ...prev,
          access_token: "",
        }));

        await loadSettings();

      } else {

        setErrorMessage(
          response.data?.message ||
          "Failed to save settings"
        );

      }

    } catch (error) {

      console.error(
        "WhatsApp save error:",
        error
      );

      setErrorMessage(
        error.response?.data?.message ||
        "Failed to save WhatsApp settings"
      );

    } finally {

      setLoading(false);

    }
  };


  /*
  |--------------------------------------------------------------------------
  | TEST CONNECTION
  |--------------------------------------------------------------------------
  */
  const handleTestConnection =
    async () => {

      if (!clientId) return;

      setLoading(true);

      try {

        const response =
          await axiosInstance.post(
            `/api/whatsapp-cloud/test/${clientId}`
          );

        if (
          response.data?.success
        ) {

          setConnected(true);

          setShowSuccess(true);

          await loadSettings();

        } else {

          setErrorMessage(
            response.data?.message ||
            "WhatsApp connection failed"
          );

        }

      } catch (error) {

        console.error(
          "WhatsApp test error:",
          error
        );

        setErrorMessage(
          error.response?.data?.message ||
          "WhatsApp connection failed"
        );

      } finally {

        setLoading(false);

      }
    };


  /*
  |--------------------------------------------------------------------------
  | DISCONNECT
  |--------------------------------------------------------------------------
  */
  const handleDisconnect =
    async () => {

      if (!clientId) return;

      setLoading(true);

      try {

        const response =
          await axiosInstance.post(
            `/api/whatsapp-cloud/disconnect/${clientId}`
          );

        if (
          response.data?.success
        ) {

          setConnected(false);

          setForm((prev) => ({
            ...prev,
            access_token: "",
          }));

          setShowSuccess(true);

          await loadSettings();

        } else {

          setErrorMessage(
            response.data?.message ||
            "Disconnect failed"
          );

        }

      } catch (error) {

        console.error(
          "WhatsApp disconnect error:",
          error
        );

        setErrorMessage(
          error.response?.data?.message ||
          "Failed to disconnect WhatsApp"
        );

      } finally {

        setLoading(false);

      }
    };


  return (
    <div className="p-6 bg-white rounded shadow-md max-w-5xl mx-auto">

      <div className="flex items-center justify-between mb-6">

        <h2 className="text-xl font-semibold">
          WhatsApp Cloud API
        </h2>

        <div
          className={`px-3 py-1 rounded text-sm font-medium ${
            connected
              ? "bg-green-100 text-green-700"
              : "bg-gray-100 text-gray-600"
          }`}
        >
          {connected
            ? "● Connected"
            : "● Not Connected"}
        </div>

      </div>


      {/* BUSINESS NAME */}

      <div className="mb-4">

        <label className="block text-sm text-gray-600 mb-1">
          WhatsApp Business Name
        </label>

        <input
          type="text"
          name="business_name"
          value={form.business_name}
          onChange={handleChange}
          className="border border-gray-300 rounded px-3 py-2 w-full text-sm"
          placeholder="Business name"
        />

      </div>


      {/* DISPLAY PHONE */}

      <div className="mb-4">

        <label className="block text-sm text-gray-600 mb-1">
          WhatsApp Business Number
        </label>

        <input
          type="text"
          name="display_phone_number"
          value={form.display_phone_number}
          onChange={handleChange}
          className="border border-gray-300 rounded px-3 py-2 w-full text-sm"
          placeholder="+91 XXXXX XXXXX"
        />

      </div>


      {/* TWO COLUMN */}

      <div className="grid grid-cols-2 gap-6">

        <div>

          <label className="block text-sm text-gray-600 mb-1">
            WABA ID
          </label>

          <input
            type="text"
            name="waba_id"
            value={form.waba_id}
            onChange={handleChange}
            className="border border-gray-300 rounded px-3 py-2 w-full text-sm"
            placeholder="WhatsApp Business Account ID"
          />

        </div>


        <div>

          <label className="block text-sm text-gray-600 mb-1">
            Phone Number ID
          </label>

          <input
            type="text"
            name="phone_number_id"
            value={form.phone_number_id}
            onChange={handleChange}
            className="border border-gray-300 rounded px-3 py-2 w-full text-sm"
            placeholder="Meta Phone Number ID"
          />

        </div>

      </div>


      {/* ACCESS TOKEN */}

      <div className="mt-5">

        <label className="block text-sm text-gray-600 mb-1">
          Meta Access Token
        </label>

        <input
          type="password"
          name="access_token"
          value={form.access_token}
          onChange={handleChange}
          className="border border-gray-300 rounded px-3 py-2 w-full text-sm"
          placeholder={
            connected
              ? "Leave blank to keep existing token"
              : "Enter Meta access token"
          }
        />

        <p className="text-xs text-gray-500 mt-1">
          The access token is encrypted before being stored.
        </p>

      </div>


      {/* BUTTONS */}

      <div className="flex gap-3 mt-6">

        <button
          onClick={handleSave}
          disabled={loading}
          className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {loading
            ? "Saving..."
            : "Save"}
        </button>


        <button
          onClick={handleTestConnection}
          disabled={loading || !form.phone_number_id}
          className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
        >
          Test Connection
        </button>


        {connected && (

          <button
            onClick={handleDisconnect}
            disabled={loading}
            className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700 disabled:opacity-50"
          >
            Disconnect
          </button>

        )}

      </div>


      {/* SUCCESS */}

      <Snackbar
        open={showSuccess}
        autoHideDuration={3000}
        onClose={() =>
          setShowSuccess(false)
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >

        <MuiAlert
          elevation={6}
          severity="success"
          variant="filled"
        >
          WhatsApp Cloud API operation successful.
        </MuiAlert>

      </Snackbar>


      {/* ERROR */}

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() =>
          setErrorMessage("")
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >

        <MuiAlert
          elevation={6}
          severity="error"
          variant="filled"
        >
          {errorMessage}
        </MuiAlert>

      </Snackbar>

    </div>
  );
}